import { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { BolDocumentIcon, VehicleNumberIcon } from 'components/icons/OcrValueSourceIcons';
import { formatOcrValueTime } from 'utils/ocrVerification';
import styles from './OcrValuesSection.module.css';

// Figma draws the header glyphs at 13px; 12px is the size at which they render sharp (see OcrValueSourceIcons).
const GROUP_HEADER = {
    BOL: { title: 'BOL document', Icon: BolDocumentIcon },
    VEHICLE: { title: 'Numbers on vehicle', Icon: VehicleNumberIcon },
};

const SOURCE_LABEL = { CONDOR: 'CONDOR', MACAW: 'ALISA' };

/**
 * The OCR values of a verified field (Figma 16362:41274), grouped as "BOL document" and "Numbers on vehicle". Rows
 * with a value are picked like the other suggestions (`selectableIndex` is their keyboard position); unknown rows
 * are shown but cannot be picked.
 */
function OcrValuesSection({ groups, onSelect, selectedIndex, activeIndex }) {
    return (
        <div className={styles.section}>
            {groups.map(({ key, rows }) => {
                const { title, Icon } = GROUP_HEADER[key];
                return (
                    <div key={key} className={styles.group} role="group" aria-label={title}>
                        <div className={styles['group-header']}>
                            <Icon />
                            <span>{title}</span>
                        </div>
                        {rows.map((row) => (
                            <OcrValueRow
                                key={row.key}
                                row={row}
                                selected={row.selectableIndex >= 0 && row.selectableIndex === selectedIndex}
                                active={row.selectableIndex >= 0 && row.selectableIndex === activeIndex}
                                onSelect={onSelect}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function OcrValueRow({ row, selected, active, onSelect }) {
    const rowRef = useRef(null);
    const isSelectable = row.selectableIndex >= 0;

    useEffect(() => {
        if (selected) rowRef.current?.scrollIntoView({ block: 'nearest', inline: 'start' });
    }, [selected]);

    const classNames = [styles.row];
    if (isSelectable) classNames.push(styles['row--selectable']);
    if (selected) classNames.push(styles['row--selected']);
    if (active) classNames.push(styles['row--active']);
    const valueClass = `${styles.value}${row.tone ? ` ${styles[`value--${row.tone}`]}` : ''}`;
    const time = formatOcrValueTime(row.timestamp);

    return (
        <div
            ref={rowRef}
            className={classNames.join(' ')}
            role="option"
            aria-selected={selected}
            aria-disabled={!isSelectable}
            onMouseDown={isSelectable ? () => onSelect(row.value, row.selectableIndex) : undefined}
        >
            <span className={valueClass}>{row.value ?? 'unknown'}</span>
            <span className={styles.meta}>
                {row.cameraName ? `${row.cameraName} · ` : ''}
                {SOURCE_LABEL[row.source]}
                {time && <span className={styles['meta-time']}> {time}</span>}
            </span>
        </div>
    );
}

const rowShape = PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string,
    source: PropTypes.oneOf(['CONDOR', 'MACAW']).isRequired,
    cameraName: PropTypes.string,
    timestamp: PropTypes.number,
    tone: PropTypes.oneOf(['match', 'mismatch']),
    selectableIndex: PropTypes.number.isRequired,
});

OcrValueRow.propTypes = {
    row: rowShape.isRequired,
    selected: PropTypes.bool,
    active: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
};

OcrValuesSection.propTypes = {
    groups: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.oneOf(['BOL', 'VEHICLE']).isRequired, rows: PropTypes.arrayOf(rowShape).isRequired }))
        .isRequired,
    onSelect: PropTypes.func.isRequired,
    selectedIndex: PropTypes.number,
    activeIndex: PropTypes.number,
};

export default memo(OcrValuesSection);
